import { Suspense, lazy } from "react";
import Index from "@/pages/Index";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AnalyticsProvider } from "@/hooks/useAnalytics";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Public
const About = lazy(() => import("./pages/About"));
const Resources = lazy(() => import("./pages/Resources"));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const EndUserTerms = lazy(() => import("./pages/EndUserTerms"));
const LeasingAddendum = lazy(() => import("./pages/LeasingAddendum"));
const CameraAIAddendum = lazy(() => import("./pages/CameraAIAddendum"));
const AlarmFireAddendum = lazy(() => import("./pages/AlarmFireAddendum"));
const Reschedule = lazy(() => import("./pages/Reschedule"));
const CustomerQuoteView = lazy(() => import("./pages/CustomerQuoteView"));
const SignAgreement = lazy(() => import("./pages/SignAgreement"));
const AIQuote = lazy(() => import("./pages/AIQuote"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Template = lazy(() => import("./pages/Template"));
const Services = lazy(() => import("./pages/Services"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const AdminAIQuotes = lazy(() => import("./pages/AdminAIQuotes").then(m => ({ default: m.AdminAIQuotes })));
const AdminAIQuoteDetail = lazy(() => import("./pages/AdminAIQuotes").then(m => ({ default: m.AdminAIQuoteDetail })));

// Admin
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminResetPassword = lazy(() => import("./pages/AdminResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminResources = lazy(() => import("./pages/AdminResources"));
const AdminResourceEditor = lazy(() => import("./pages/AdminResourceEditor"));
const ContactDetail = lazy(() => import("./pages/ContactDetail"));
const AppointmentsCalendar = lazy(() => import("./pages/AppointmentsCalendar"));
const AppointmentsList = lazy(() => import("./pages/AppointmentsList"));
const AdminCalendar = lazy(() => import("./pages/AdminCalendar"));
const AppointmentDetail = lazy(() => import("./pages/AppointmentDetail"));
const Activities = lazy(() => import("./pages/Activities"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const UserApprovals = lazy(() => import("./pages/UserApprovals"));
const AdminQuotes = lazy(() => import("./pages/AdminQuotes"));
const AdminQuoteBuilder = lazy(() => import("./pages/AdminQuoteBuilder"));
const AdminQuoteDetail = lazy(() => import("./pages/AdminQuoteDetail"));
const AdminProducts = lazy(() => import("./pages/AdminProducts"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const AdminChat = lazy(() => import("./pages/AdminChat"));
const AdminAgreements = lazy(() => import("./pages/AdminAgreements"));
const AdminTasks = lazy(() => import("./pages/AdminTasks"));
const AdminTradeMode = lazy(() => import("./pages/AdminTradeMode"));
const AdminOnboarding = lazy(() => import("./pages/AdminOnboarding"));
const AdminAIFeedback = lazy(() => import("./pages/AdminAIFeedback"));
const AdminOrderForm = lazy(() => import("./pages/AdminOrderForm"));
const AdminAIAssistant = lazy(() => import("./pages/AdminAIAssistant"));
const AdminIntegrations = lazy(() => import("./pages/AdminIntegrations"));
const AdminEmailSetup = lazy(() => import("./pages/AdminEmailSetup"));
const AdminWalkthrough = lazy(() => import("./pages/AdminWalkthrough"));
const TemplateWalkthrough = lazy(() => import("./pages/TemplateWalkthrough"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const AdminBilling = lazy(() => import("./pages/AdminBilling"));
const Field = lazy(() => import("./pages/Field"));
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AnalyticsProvider>
          <ScrollToTop />
          <PaymentTestModeBanner />
          <ErrorBoundary>
            <Suspense
              fallback={
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
                  Loading...
                </div>
              }
            >
              <Routes>
                <Route path="/" element={<Index />} />

                {/* Public pages */}
                <Route path="/about-us" element={<About />} />
                <Route path="/blog" element={<Resources />} />
                <Route path="/blog/:slug" element={<ResourceDetail />} />
                {/* Same-site rename: old /resources URLs forward to /blog */}
                <Route path="/resources" element={<Navigate to="/blog" replace />} />
                <Route path="/resources/:slug" element={<NavigateBlogSlug />} />
                <Route path="/contact-us" element={<Contact />} />
                <Route path="/ai-quote" element={<AIQuote />} />
                <Route path="/ai-qoute" element={<Navigate to="/ai-quote" replace />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/:slug" element={<ServicePage />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/template" element={<Template />} />
                <Route path="/field" element={<Field />}/>
                <Route path="/template/walkthrough" element={<TemplateWalkthrough />} />
                <Route path="/walkthrough" element={<TemplateWalkthrough />} />

                {/* Legal */}
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/legal/end-user-terms" element={<EndUserTerms />} />
                <Route path="/legal/leasing-addendum" element={<LeasingAddendum />} />
                <Route path="/legal/camera-ai-addendum" element={<CameraAIAddendum />} />
                <Route path="/legal/alarm-fire-addendum" element={<AlarmFireAddendum />} />

                {/* Customer-facing flows */}
                <Route path="/reschedule/:appointmentId" element={<Reschedule />} />
                <Route path="/quotes/:id" element={<CustomerQuoteView />} />
                <Route path="/sign/:token" element={<SignAgreement />} />

                {/* Admin Login (Public) */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/reset-password" element={<AdminResetPassword />} />

                {/* Protected Admin Routes */}
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/contacts" element={<ProtectedRoute><AdminContacts /></ProtectedRoute>} />
                <Route path="/admin/contacts/:id" element={<ProtectedRoute><ContactDetail /></ProtectedRoute>} />
                <Route path="/admin/appointments" element={<ProtectedRoute><AppointmentsCalendar /></ProtectedRoute>} />
                <Route path="/admin/appointments/list" element={<ProtectedRoute><AppointmentsList /></ProtectedRoute>} />
                <Route path="/admin/calendar" element={<ProtectedRoute><AdminCalendar /></ProtectedRoute>} />
                <Route path="/admin/appointments/:id" element={<ProtectedRoute><AppointmentDetail /></ProtectedRoute>} />
                <Route path="/admin/activities" element={<ProtectedRoute><Activities /></ProtectedRoute>} />
                <Route path="/admin/user-approvals" element={<ProtectedRoute><UserApprovals /></ProtectedRoute>} />
                <Route path="/admin/user-management" element={<ProtectedRoute><UserManagement /></ProtectedRoute>} />
                <Route path="/admin/resources" element={<ProtectedRoute><AdminResources /></ProtectedRoute>} />
                <Route path="/admin/resources/new" element={<ProtectedRoute><AdminResourceEditor /></ProtectedRoute>} />
                <Route path="/admin/resources/edit/:id" element={<ProtectedRoute><AdminResourceEditor /></ProtectedRoute>} />
                <Route path="/admin/quotes" element={<ProtectedRoute><AdminQuotes /></ProtectedRoute>} />
                <Route path="/admin/quotes/new" element={<ProtectedRoute><AdminQuoteBuilder /></ProtectedRoute>} />
                <Route path="/admin/quotes/:id" element={<ProtectedRoute><AdminQuoteDetail /></ProtectedRoute>} />
                <Route path="/admin/quotes/:id/edit" element={<ProtectedRoute><AdminQuoteBuilder /></ProtectedRoute>} />
                <Route path="/admin/products" element={<ProtectedRoute><AdminProducts /></ProtectedRoute>} />
                <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
                <Route path="/admin/chat" element={<ProtectedRoute><AdminChat /></ProtectedRoute>} />
                <Route path="/admin/tasks" element={<ProtectedRoute><AdminTasks /></ProtectedRoute>} />
                <Route path="/admin/agreements" element={<ProtectedRoute><AdminAgreements /></ProtectedRoute>} />
                <Route path="/admin/order-form" element={<ProtectedRoute><AdminOrderForm /></ProtectedRoute>} />
                <Route path="/admin/ai-quotes" element={<ProtectedRoute><AdminAIQuotes /></ProtectedRoute>} />
                <Route path="/admin/ai-quotes/:id" element={<ProtectedRoute><AdminAIQuoteDetail /></ProtectedRoute>} />
                <Route path="/admin/trade-mode" element={<ProtectedRoute><AdminTradeMode /></ProtectedRoute>} />
                <Route path="/admin/onboarding" element={<ProtectedRoute><AdminOnboarding /></ProtectedRoute>} />
                <Route path="/admin/ai-feedback" element={<ProtectedRoute><AdminAIFeedback /></ProtectedRoute>} />
                <Route path="/admin/assistant" element={<ProtectedRoute><AdminAIAssistant /></ProtectedRoute>} />
                <Route path="/admin/integrations" element={<ProtectedRoute><AdminIntegrations /></ProtectedRoute>} />
                <Route path="/admin/email-setup" element={<ProtectedRoute><AdminEmailSetup /></ProtectedRoute>} />
                <Route path="/admin/walkthrough" element={<ProtectedRoute><AdminWalkthrough /></ProtectedRoute>} />
                <Route path="/admin/billing" element={<ProtectedRoute><AdminBilling /></ProtectedRoute>} />

                {/* Catch-all */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </AnalyticsProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

// Same-site rename helper: /resources/:slug → /blog/:slug
import { useParams } from "react-router-dom";
function NavigateBlogSlug() {
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug ?? ""}`} replace />;
}

export default App;
